import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  theme,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { fetchCswapStatus } from "../api/cswapApi";

const STATUS = {
  ok: ["Available", "green"],
  login_required: ["Reconnect needed", "red"],
  unavailable: ["Usage unavailable", "default"],
  stale: ["Update overdue", "orange"],
};
const date = (value) =>
  value ? new Date(value * 1000).toLocaleString() : "Not measured";

export default function CswapPage() {
  const { token } = theme.useToken();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("all");
  const [now, setNow] = useState(Date.now() / 1000);
  const reload = useRef(() => {});

  useEffect(() => {
    let alive = true;
    let pending = false;
    let lastAttempt = 0;
    const load = async () => {
      if (pending) return;
      pending = true;
      lastAttempt = Date.now();
      setLoading(true);
      try {
        const result = await fetchCswapStatus();
        if (alive) {
          setData(result);
          setError(null);
        }
      } catch (failure) {
        if (alive) {
          setError(failure.status === 403 ? "denied" : "unavailable");
          if (failure.status === 403 || failure.status === 401) setData(null);
        }
      } finally {
        pending = false;
        if (alive) setLoading(false);
      }
    };
    reload.current = load;
    const tick = () => {
      setNow(Date.now() / 1000);
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastAttempt >= 300000
      )
        load();
    };
    load();
    const timer = setInterval(tick, 30000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  const rows = (data?.accounts ?? [])
    .map((row) => ({
      ...row,
      status: error
        ? "unavailable"
        : row.checked_at && now - row.checked_at > 600
          ? "stale"
          : row.status,
    }))
    .filter((row) => provider === "all" || row.provider === provider);
  const columns = [
    {
      title: "Account",
      key: "account",
      render: (_, row) => (
        <>
          <div className="font-medium">{row.email}</div>
          <Typography.Text type="secondary">
            {row.provider} · {row.plan}
          </Typography.Text>
          <div>
            <Tag>{row.shared ? "Shared" : "Private"}</Tag>
          </div>
        </>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, row) =>
        row.windows.length ? (
          <Space direction="vertical" className="w-full">
            {row.windows.map((window, i) => (
              <div key={i} style={{ minWidth: 180 }}>
                <span>
                  {window.label} used {row.status !== "ok" && "(last measured)"}
                </span>
                <Progress
                  percent={window.used_pct}
                  size="small"
                  status={
                    row.status === "ok"
                      ? window.used_pct >= 95
                        ? "exception"
                        : "normal"
                      : "normal"
                  }
                  strokeColor={
                    row.status === "ok" ? undefined : token.colorTextDisabled
                  }
                />
                <Typography.Text type="secondary">
                  Resets {date(window.resets_at)}
                </Typography.Text>
              </div>
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary">No measurement</Typography.Text>
        ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, row) => {
        const limited =
          row.status === "ok" && row.windows.some((w) => w.used_pct >= 95);
        const exhausted =
          row.status === "ok" && row.windows.some((w) => w.used_pct >= 100);
        const [label, color] = exhausted
          ? ["Quota exhausted", "red"]
          : limited
            ? ["Low capacity", "orange"]
            : (STATUS[row.status] ?? STATUS.unavailable);
        return (
          <>
            <Tag color={color}>{label}</Tag>
            <div className="text-xs text-gray-500 mt-2">
              Checked {date(row.checked_at)}
              {row.checked_at && (
                <div>
                  {Math.max(0, Math.floor((now - row.checked_at) / 60))} minutes
                  ago
                </div>
              )}
            </div>
          </>
        );
      },
    },
    {
      title: "Reconnect",
      key: "reconnect",
      render: (_, row) =>
        row.shared ? (
          <Typography.Text
            code
            copyable
          >{`scripts/cswap shared repair ${row.id}`}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            Manage in your terminal
          </Typography.Text>
        ),
    },
  ];

  return (
    <main className="ml-[240px] p-8 min-h-screen">
      <h1 className="text-3xl font-bold mb-2">Account usage</h1>
      <p className="text-gray-500 mb-6">
        Shared Claude and Codex accounts. Updates every five minutes while this
        page is open.
      </p>
      <Alert
        className="mb-4"
        type="info"
        showIcon
        message="Use shared accounts in your terminal"
        description={
          <Space direction="vertical">
            <span>
              Update your DashONE checkout and follow tools/cswap/README.md
              once. Use your own AWS CLI login with access to the shared
              accounts.
            </span>
            <Typography.Text code copyable>
              scripts/cswap codex live
            </Typography.Text>
            <span>
              Starts on the shared account with the lowest verified usage.
              Automatic switching keeps your conversation open. In Codex, use
              $cswap; in Claude Code, use /cswap.
            </span>
          </Space>
        }
      />
      {error && (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message={
            error === "denied"
              ? "Access denied. Sign in with a verified Dashmote staff account."
              : "Could not update account usage. Previous measurements may be out of date."
          }
        />
      )}
      <div className="bg-white rounded-2xl border p-6">
        <Space className="mb-5" wrap>
          <Select
            aria-label="Provider"
            value={provider}
            onChange={setProvider}
            style={{ width: 160 }}
            options={[
              { value: "all", label: "All providers" },
              { value: "Codex", label: "Codex" },
              { value: "Claude", label: "Claude" },
            ]}
          />
          <Button
            aria-label="Refresh"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => reload.current()}
          >
            Refresh
          </Button>
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading && !data}
          pagination={false}
          scroll={{ x: 1000 }}
          locale={{ emptyText: <Empty description="No accounts to display" /> }}
        />
      </div>
      <p className="text-sm text-gray-500 mt-4">
        If an account stops working, copy its reconnect command into your
        terminal. It checks the shared login first and guides you through
        sign-in when needed.
      </p>
    </main>
  );
}
